import React from 'react';
import {
  FaChartBar, FaUserMd, FaCalendar, FaDollarSign, FaUserInjured,
  FaCalendarAlt, FaClipboardList, FaHospital, FaHourglassHalf,
  FaCreditCard, FaReceipt, FaFileInvoice, FaFileMedical, FaPills,
  FaChartLine, FaFileAlt, FaEnvelope, FaInbox, FaEnvelopeOpenText,
  FaEdit, FaPencilAlt, FaAddressBook, FaMicrophone, FaBrain, FaRobot,
  FaClipboard, FaNotesMedical, FaMapMarkerAlt, FaStethoscope,
  FaFileSignature, FaFolder,
  FaUsers, FaBook, FaClipboardCheck,
  FaCalculator, FaWrench, FaLock,
  FaUserShield, FaBuilding, FaClock, FaHouseDamage, FaDoorOpen,
  FaUserNurse, FaSyringe, FaCalendarCheck, FaTag, FaListAlt,
  FaHome, FaSearch
} from 'react-icons/fa';

/**
 * Centralized icon configuration for the entire application
 * This ensures consistency between Sidebar, Breadcrumbs, and Page headings
 */

export interface IconConfig {
  icon: React.ReactElement;
  displayName?: string;
}

/**
 * Route-to-Icon mapping
 * Key: route path segment (e.g., 'patients', 'billing', 'invoices')
 * Value: Icon configuration with optional display name override
 */
export const routeIconMap: Record<string, IconConfig> = {
  // Home
  'home': { icon: <FaHome className="text-primary" />, displayName: 'Home' },
  
  // Dashboards
  'dashboards': { icon: <FaChartBar className="text-primary" />, displayName: 'Dashboards' },
  'clinician': { icon: <FaUserMd className="text-primary" />, displayName: 'Clinician' },
  'billing': { icon: <FaDollarSign className="text-primary" />, displayName: 'Billing' },
  
  // Patients
  'patients': { icon: <FaUserInjured className="text-primary" />, displayName: 'Patients' },
  
  // Scheduling/Appointments
  'scheduling': { icon: <FaCalendar className="text-primary" />, displayName: 'Scheduling' },
  'appointments': { icon: <FaCalendarAlt className="text-primary" />, displayName: 'Appointments' },
  'waitinglist': { icon: <FaClipboardList className="text-primary" />, displayName: 'Waiting List' },
  'bookedprocedures': { icon: <FaHospital className="text-primary" />, displayName: 'Booked Procedures' },
  'waitingroom': { icon: <FaHourglassHalf className="text-primary" />, displayName: 'Waiting Room' },
  'appointmenttypes': { icon: <FaCalendarCheck className="text-primary" />, displayName: 'Appointment Types' },
  
  // Billing (when used as parent path, use credit card icon)
  'invoices': { icon: <FaReceipt className="text-primary" />, displayName: 'Invoices' },
  'eclipse-claims': { icon: <FaHospital className="text-primary" />, displayName: 'Eclipse Claims' },
  'templates': { icon: <FaClipboardList className="text-primary" />, displayName: 'Templates' },
  'mbs-items': { icon: <FaPills className="text-primary" />, displayName: 'MBS Items' },
  
  // Accounting
  'accounts': { icon: <FaChartLine className="text-primary" />, displayName: 'Accounting' },
  'chart-of-accounts': { icon: <FaFileAlt className="text-primary" />, displayName: 'Chart of Accounts' },
  'journal-entries': { icon: <FaBook className="text-primary" />, displayName: 'Journal Entries' },
  
  // Documents
  'documents': { icon: <FaFileAlt className="text-primary" />, displayName: 'Documents' },
  'documenttypes': { icon: <FaFileSignature className="text-primary" />, displayName: 'Document Types' },
  'documentcategories': { icon: <FaFolder className="text-primary" />, displayName: 'Document Categories' },
  
  // Email
  'email': { icon: <FaEnvelope className="text-primary" />, displayName: 'Email' },
  'compose': { icon: <FaEnvelopeOpenText className="text-primary" />, displayName: 'Compose' },
  'signatures': { icon: <FaPencilAlt className="text-primary" />, displayName: 'Signatures' },
  
  // Other
  'addressbook': { icon: <FaAddressBook className="text-primary" />, displayName: 'Address Book' },
  'dictations': { icon: <FaMicrophone className="text-primary" />, displayName: 'Dictations' },
  'ai': { icon: <FaBrain className="text-primary" />, displayName: 'AI' },
  'integration': { icon: <FaBrain className="text-primary" />, displayName: 'Integration Hub' },
  'assistant': { icon: <FaRobot className="text-primary" />, displayName: 'AI Clinical Assistant' },
  
  // Clinical Assessment
  'clinicalassessment': { icon: <FaStethoscope className="text-primary" />, displayName: 'Clinical Assessment' },
  'painworkupsheet': { icon: <FaClipboard className="text-primary" />, displayName: 'Pain Workup Sheet' },
  'standardworkupsheet': { icon: <FaNotesMedical className="text-primary" />, displayName: 'Standard Workup Sheet' },
  
  // Body Diagrams
  'bodydiagrams': { icon: <FaMapMarkerAlt className="text-primary" />, displayName: 'Body Diagrams' },
  'createbodypainpointchart': { icon: <FaMapMarkerAlt className="text-primary" />, displayName: 'Create Body Pain Point Chart' },
  
  // Clinical Outcomes
  'clinicaloutcomes': { icon: <FaStethoscope className="text-primary" />, displayName: 'Clinical Outcomes' },

  // Publications
  'publications': { icon: <FaBook className="text-primary" />, displayName: 'Publications' },
  'search': { icon: <FaSearch className="text-primary" />, displayName: 'Publication Search' },
  'publicationsearch': { icon: <FaSearch className="text-primary" />, displayName: 'Publication Search' },

  // Tools
  'tools': { icon: <FaWrench className="text-primary" />, displayName: 'Tools' },
  'requeststracker': { icon: <FaClipboardCheck className="text-primary" />, displayName: 'Requests Tracker' },
  'opioidcalculator': { icon: <FaCalculator className="text-primary" />, displayName: 'Opioid Calculator' },
  
  // Security
  'security': { icon: <FaLock className="text-primary" />, displayName: 'Security' },
  'users': { icon: <FaUsers className="text-primary" />, displayName: 'Users' },
  'groups': { icon: <FaUserShield className="text-primary" />, displayName: 'Groups' },
  
  // Facilities
  'facilities': { icon: <FaBuilding className="text-primary" />, displayName: 'Facilities' },
  'clients': { icon: <FaBuilding className="text-primary" />, displayName: 'Clients' },
  'clientsettings': { icon: <FaBuilding className="text-primary" />, displayName: 'Client Settings' },
  'clinic': { icon: <FaHospital className="text-primary" />, displayName: 'Clinic' },
  'facilitytypes': { icon: <FaTag className="text-primary" />, displayName: 'Facility Types' },
  'facilityroomtypes': { icon: <FaDoorOpen className="text-primary" />, displayName: 'Facility Room Types' },
  'facilityrooms': { icon: <FaDoorOpen className="text-primary" />, displayName: 'Facility Rooms' },
  
  // Providers
  'providers': { icon: <FaUserNurse className="text-primary" />, displayName: 'Providers' },
  'providertypes': { icon: <FaUserShield className="text-primary" />, displayName: 'Provider Types' },
  'sessions': { icon: <FaClock className="text-primary" />, displayName: 'Sessions' },
  'sessionrosters': { icon: <FaClock className="text-primary" />, displayName: 'Session Rosters' },
  'sessiontypes': { icon: <FaClipboardList className="text-primary" />, displayName: 'Session Types' },
  
  // Procedures
  'procedures': { icon: <FaSyringe className="text-primary" />, displayName: 'Procedures' },
  'proceduretypes': { icon: <FaSyringe className="text-primary" />, displayName: 'Procedure Types' },
  'proceduretypecategories': { icon: <FaListAlt className="text-primary" />, displayName: 'Procedure Type Categories' },

  // Letters
  'letters': { icon: <FaMicrophone className="text-primary" />, displayName: 'Letters' },
  'dictation-types': { icon: <FaFileAlt className="text-primary" />, displayName: 'Dictation Types' },
};

/**
 * Get icon configuration for a route path segment
 * @param pathSegment - The route path segment (e.g., 'patients', 'invoices')
 * @returns Icon configuration or null if not found
 */
export const getIconForRoute = (pathSegment: string): IconConfig | null => {
  return routeIconMap[pathSegment.toLowerCase()] || null;
};

/**
 * Get display name for a route path segment
 * Uses the iconConfig display name if available, otherwise capitalizes the path segment
 * @param pathSegment - The route path segment
 * @returns Formatted display name
 */
export const getDisplayName = (pathSegment: string): string => {
  const config = getIconForRoute(pathSegment);
  if (config?.displayName) {
    return config.displayName;
  }
  
  // Check for UUID pattern
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(pathSegment)) {
    return 'Patient Details';
  }
  
  // Default: capitalize first letter
  return pathSegment.charAt(0).toUpperCase() + pathSegment.slice(1);
};
